import React, { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import { getLocalApiUrl, authFetch } from '../../config/api'
import { useAuth } from '../../contexts/AuthContext'
import { formatSydneyDateTime } from '../../utils/sydneyDateTime'

const ROLE_LABELS = {
  duty_manager: 'Duty Manager',
  rgo: 'RGO',
  senior_rgo: 'Senior RGO',
}

function roleLabel(role) {
  return ROLE_LABELS[role] || role
}

export default function FloorAlerts() {
  const { roles } = useAuth()
  const [rows, setRows] = useState([])
  const [visibleRoles, setVisibleRoles] = useState([])
  const [status, setStatus] = useState('open')
  const [loading, setLoading] = useState(true)
  const [busyId, setBusyId] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await authFetch(getLocalApiUrl(`/api/mobile/floor-alerts?status=${encodeURIComponent(status)}`))
      const json = await res.json().catch(() => ({}))
      if (!res.ok || json.success === false) throw new Error(json.error || 'Could not load floor alerts')
      setRows(Array.isArray(json.data) ? json.data : [])
      setVisibleRoles(Array.isArray(json.roles) ? json.roles : [])
    } catch (err) {
      toast.error(err.message || 'Could not load floor alerts')
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [status])

  useEffect(() => { load() }, [load])

  const acknowledge = async (id) => {
    setBusyId(id)
    try {
      const res = await authFetch(getLocalApiUrl(`/api/mobile/floor-alerts/${encodeURIComponent(id)}/acknowledge`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok || json.success === false) throw new Error(json.error || 'Could not acknowledge')
      toast.success('Acknowledged')
      await load()
    } catch (err) {
      toast.error(err.message || 'Could not acknowledge')
    } finally {
      setBusyId(null)
    }
  }

  const mine = (roles || []).map((role) => String(role).toLowerCase())

  return (
    <div className="p-6 space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Floor alerts</h1>
          <p className="mt-1 max-w-2xl text-sm text-gray-600">
            Warning signs for one member or visitor on the same trading day. Each club chooses which roles fill Duty Manager, RGO, and Senior RGO in Incident Register config.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select className="input-field" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="open">Open</option>
            <option value="acknowledged">Acknowledged</option>
            <option value="all">All</option>
          </select>
          <button type="button" className="btn-secondary" onClick={load}>Refresh</button>
        </div>
      </div>

      <p className="text-xs text-gray-500">
        Your roles: {(visibleRoles.length ? visibleRoles : mine).map(roleLabel).join(', ') || 'none on this login'}
      </p>

      {loading && <p className="text-sm text-gray-500">Loading floor alerts…</p>}
      {!loading && rows.length === 0 && (
        <p className="text-sm text-gray-500">No floor alerts in this list.</p>
      )}

      <div className="space-y-3">
        {rows.map((row) => (
          <article key={row.id} className="rounded-lg border border-slate-200 bg-white p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="text-base font-semibold text-slate-900">{row.title}</h2>
                <p className="mt-1 text-sm text-slate-700">{row.person_label || row.member_id || row.visitor_id || 'Patron'}</p>
                <p className="mt-1 text-sm text-slate-600">{row.detail}</p>
              </div>
              {row.status === 'open' ? (
                <button
                  type="button"
                  className="btn-primary"
                  disabled={busyId === row.id}
                  onClick={() => acknowledge(row.id)}
                >
                  {busyId === row.id ? 'Saving…' : 'Acknowledge'}
                </button>
              ) : (
                <span className="text-xs text-emerald-700">
                  Acknowledged{row.acknowledged_role ? ` by ${roleLabel(row.acknowledged_role)}` : ''}
                  {row.acknowledged_at ? ` · ${formatSydneyDateTime(row.acknowledged_at)}` : ''}
                </span>
              )}
            </div>
            <dl className="mt-3 grid gap-2 text-xs text-slate-600 sm:grid-cols-3">
              <div>Trading day {row.trading_day || '—'}</div>
              <div>Signs {row.sign_count}</div>
              <div>Told {(row.audience_labels?.length ? row.audience_labels : row.audience_roles.map(roleLabel)).join(' or ') || '—'}</div>
              <div className="sm:col-span-3">
                Reports {row.report_ids.length ? row.report_ids.join(', ') : '—'}
                {' · '}
                <Link className="text-psg-teal" to="/compliance-safe/incident-register">Open Incident Register</Link>
              </div>
            </dl>
          </article>
        ))}
      </div>
    </div>
  )
}
